import React, { useRef, useEffect, useState } from 'react';
import { useNotificationStore, Notification, NotificationSeverity } from '../../stores/notificationStore';
import { useNavigate } from 'react-router-dom';

const severityColors: Record<NotificationSeverity, string> = {
  critical: 'text-red-400',
  warning: 'text-amber-400',
  info: 'text-blue-400',
};

const severityBg: Record<NotificationSeverity, string> = {
  critical: 'bg-red-500/10',
  warning: 'bg-amber-500/10',
  info: 'bg-blue-500/10',
};

const severityIcons: Record<NotificationSeverity, React.ReactNode> = {
  critical: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'ahora mismo';
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)} h`;
  return `hace ${Math.floor(seconds / 86400)} d`;
}

interface NotificationBellProps {
  className?: string;
}

export default function NotificationBell({ className = '' }: NotificationBellProps) {
  const { notifications, isOpen, toggleOpen, setOpen, markRead, markAllRead, unreadCount } =
    useNotificationStore();
  const ref = useRef<HTMLDivElement>(null);
  const [shake, setShake] = useState(false);
  const prevCriticalRef = useRef(0);

  const unread = unreadCount();
  const recentNotifications = notifications.slice(0, 15);
  const criticalCount = notifications.filter(
    (n) => n.severity === 'critical' && !n.read
  ).length;

  // Shake animation on new critical notifications
  useEffect(() => {
    if (criticalCount > prevCriticalRef.current) {
      setShake(true);
      const timer = setTimeout(() => setShake(false), 600);
      return () => clearTimeout(timer);
    }
    prevCriticalRef.current = criticalCount;
  }, [criticalCount]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setOpen]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Bell Button */}
      <button
        onClick={toggleOpen}
        className={`relative p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors ${
          shake ? 'animate-shake' : ''
        }`}
        aria-label="Notificaciones"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-h-[500px] bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
            <h3 className="text-sm font-semibold text-white">Notificaciones</h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-[380px]">
            {recentNotifications.length === 0 ? (
              <div className="py-12 text-center text-zinc-500">
                <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                No hay notificaciones
              </div>
            ) : (
              recentNotifications.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onRead={markRead}
                />
              ))
            )}
          </div>

        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-8deg); }
          40% { transform: rotate(8deg); }
          60% { transform: rotate(-4deg); }
          80% { transform: rotate(4deg); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}

function NotificationItem({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead: (id: string) => void;
}) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (!notification.read) onRead(notification.id);
    useNotificationStore.getState().setOpen(false);
    const href = notification.action?.href;
    // Enlaces internos con el router (una recarga completa bloquearía la sesión); externos como antes.
    if (href) { if (href.startsWith('/')) navigate(href); else window.location.href = href; }
    notification.action?.onClick?.();
  };

  return (
    <div
      onClick={handleClick}
      className={`flex gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50 ${
        !notification.read ? severityBg[notification.severity] : ''
      }`}
    >
      <div className={`mt-0.5 flex-shrink-0 ${severityColors[notification.severity]}`}>
        {severityIcons[notification.severity]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium truncate ${!notification.read ? 'text-white' : 'text-zinc-300'}`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-emerald-400" />
          )}
        </div>
        <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notification.message}</p>
        <p className="text-[10px] text-zinc-500 mt-1">{timeAgo(notification.createdAt)}</p>
      </div>
    </div>
  );
}
